import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { cbtAPI } from '../../api';

const ExcelUpload = () => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [serverData, setServerData] = useState([]);
  const [allowedTestIds, setAllowedTestIds] = useState([]);
  const [importedCount, setImportedCount] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  const { cbt_pss_code } = useParams();
  const requiredHeaders = ['test_id', 'test_information'];

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Fetch server data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await cbtAPI.getStudentResults(cbt_pss_code);
        
        if (response.success && Array.isArray(response.data)) {
          setServerData(response.data);
          
          const uniqueTestIds = [...new Set(response.data
            .map(item => item.test_id)
            .filter(Boolean))];
          setAllowedTestIds(uniqueTestIds);
          setImportedCount(response.data.length || 0);
        } else {
          // toast.warning('No student data found for this CBT');
          setServerData([]);
        }
      } catch (error) {
        console.error('Fetch error:', error);
        const errorMsg = error.data?.detail || error.message || 'Failed to load student data';
        toast.error(errorMsg);
        setServerData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (cbt_pss_code) {
      fetchData();
    }
  }, [cbt_pss_code]);

  // Drag and drop handlers
  const handleDragEvents = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files[0]) validateAndSetFile(e.dataTransfer.files[0]);
  }, []);

  // File validation and processing
  const validateAndSetFile = (file) => {
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      toast.error('Invalid file type. Please upload Excel or CSV.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Max 10MB.');
      return;
    }
    setFile(file);
    readExcelPreview(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) validateAndSetFile(e.target.files[0]);
  };

  const readExcelPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });

        if (jsonData.length < 2) {
          toast.error('File is empty or has no data rows.');
          return;
        }

        const headers = jsonData[0].map(h => String(h).trim().toLowerCase());
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h.toLowerCase()));

        if (missingHeaders.length) {
          toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
          return;
        }

        const testIdIndex = headers.findIndex(h => h === 'test_id');
        const testInfoIndex = headers.findIndex(h => h === 'test_information');

        const processedData = jsonData.slice(1).filter(row => 
          row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '')
        ).map(row => {
          const originalTestId = row[testIdIndex] !== undefined ? String(row[testIdIndex]).trim() : '';
          const sanitizedTestId = originalTestId.replace(/\s+/g, '-').toLowerCase();
          
          return [
            cbt_pss_code,
            sanitizedTestId,
            row[testInfoIndex] !== undefined ? String(row[testInfoIndex]).trim() : ''
          ];
        });

        if (processedData.length === 0) {
          toast.error('No valid data rows found in file.');
          return;
        }

        setPreviewData(processedData.slice(0, 10));
        toast.info(`Loaded ${processedData.length} rows for preview`);
      } catch (err) {
        console.error('Error reading file:', err);
        toast.error('Error reading file. Please check file format.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select a file.');

    setIsLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet);
          
          const studentsData = jsonData.map(row => {
            let testId = String(row.test_id || '').trim();
            testId = testId.replace(/\s+/g, '-').toLowerCase();
            
            return {
              cbt_pass_code: cbt_pss_code,
              test_id: testId,
              test_information: String(row.test_information || '').trim()
            };
          });

          const uniqueData = [];
          const seenTestIds = new Set();
          const duplicatesInFile = [];

          studentsData.forEach((student, index) => {
            if (!student.test_id) {
              toast.error(`Row ${index + 2}: Test ID is required`);
              return;
            }

            if (seenTestIds.has(student.test_id)) {
              duplicatesInFile.push(`Row ${index + 2}: Test ID "${student.test_id}" is duplicate`);
            } else {
              seenTestIds.add(student.test_id);
              uniqueData.push(student);
            }
          });

          if (duplicatesInFile.length > 0) {
            toast.warning(`Found ${duplicatesInFile.length} duplicates in file. They will be skipped.`);
          }

          const existingTestIds = new Set(allowedTestIds);
          const finalData = uniqueData.filter(student => {
            if (existingTestIds.has(student.test_id)) {
              toast.warning(`Test ID "${student.test_id}" already exists in system and will be skipped`);
              return false;
            }
            return true;
          });

          if (finalData.length === 0) {
            toast.error('No valid data to import after removing duplicates');
            setIsLoading(false);
            return;
          }

          const response = await cbtAPI.importStudents(cbt_pss_code, finalData);

          if (response.success) {
            const message = response.data?.message || `Successfully imported ${finalData.length} students!`;
            toast.success(message);
            
            const refreshResponse = await cbtAPI.getStudentResults(cbt_pss_code);
            if (refreshResponse.success && Array.isArray(refreshResponse.data)) {
              setServerData(refreshResponse.data);
              setImportedCount(refreshResponse.data.length || 0);
              
              const uniqueTestIds = [...new Set(refreshResponse.data
                .map(item => item.test_id)
                .filter(Boolean))];
              setAllowedTestIds(uniqueTestIds);
            }
            
            setFile(null);
            setPreviewData([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
          } else {
            throw new Error(response.error || 'Import failed');
          }
        } catch (parseError) {
          console.error('Parse error:', parseError);
          toast.error('Error processing file. Please check file format.');
        } finally {
          setIsLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
      
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.data?.detail || 
                          err.message || 
                          'Upload failed. Please try again.';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header Section */}
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Upload Student Data</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Upload Excel/CSV files with student information for CBT tests. 
              Required columns: <code className="bg-gray-100 px-2 py-1 rounded">test_id</code>, <code className="bg-gray-100 px-2 py-1 rounded">test_information</code>
            </p>
            <div className="flex justify-center items-center mt-4 space-x-3">
              <Link
                to={`/cbt_creator/imported-students/${cbt_pss_code}`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                View Imported Students
              </Link>
              <button
                onClick={() => navigate('/cbt_creator/dashboard')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Stats Card */}
          {/* <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-700">{importedCount}</div>
                <div className="text-sm text-blue-600 font-medium">Total Students</div>
                <div className="text-xs text-gray-500 mt-1">Imported to this CBT</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-700">{allowedTestIds.length}</div>
                <div className="text-sm text-green-600 font-medium">Unique Test IDs</div>
                <div className="text-xs text-gray-500 mt-1">Available for students</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-700">{serverData.length}</div>
                <div className="text-sm text-purple-600 font-medium">Active Records</div>
                <div className="text-xs text-gray-500 mt-1">Currently loaded</div>
              </div>
            </div>
          </div> */}

          {/* Drag & Drop Area */}
          <div 
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all mb-8
              ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'}
              ${file ? 'bg-green-50 border-green-300' : ''}`}
            onDragOver={handleDragEvents}
            onDragLeave={handleDragEvents}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="p-3 bg-indigo-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              
              <div className="space-y-1">
                <p className="font-medium text-gray-900">
                  {file ? file.name : 'Click or drag file to upload'}
                </p>
                <p className="text-sm text-gray-500">
                  {file ? 'File ready for upload' : 'Supports .xlsx, .xls, .csv (Max 10MB)'}
                </p>
              </div>
              
              <button 
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={(e) => e.stopPropagation()}
              >
                Browse Files
              </button>
            </div>
            
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx, .xls, .csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Preview Table */}
          {previewData.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z" clipRule="evenodd" />
                  </svg>
                  Preview ({previewData.length} of total rows shown)
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Review data before uploading. Invalid rows will be skipped.
                </p>
              </div>
              
              <div className="overflow-auto max-h-[500px]">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Row #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        cbt_pass_code
                      </th>
                      {requiredHeaders.map((header, i) => (
                        <th
                          key={i}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {header}
                          {header === 'test_id' && (
                            <span className="ml-1 text-red-500">*</span>
                          )}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {previewData.map((row, rowIndex) => {
                      const [cbtPassCode, testId, testInfo] = row;
                      const isValid = testId && testId.trim() !== '';
                      const isDuplicate = allowedTestIds.includes(testId);
                      
                      return (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {rowIndex + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-mono bg-gray-50">
                            {cbtPassCode}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 max-w-xs truncate" title={testId}>
                            {testId || <span className="text-gray-400 italic">empty</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 max-w-xs truncate" title={testInfo}>
                            {testInfo || <span className="text-gray-400 italic">empty</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {!isValid ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                Missing test_id
                              </span>
                            ) : isDuplicate ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Duplicate (will be skipped)
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Valid
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end mb-8">
            <button
              onClick={handleSubmit}
              disabled={!file || isLoading}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all ${
                isLoading || !file 
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white transform hover:-translate-y-0.5'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Upload Student Data
                </>
              )}
            </button>
          </div>

          {/* Server Data Sections */}
          <div className="space-y-6">
            {/* Allowed Test IDs */}
            {allowedTestIds.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-3 text-indigo-700 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Available Test IDs for Students
                </h3>
                <p className="text-sm text-gray-600 mb-4">Students can use these Test IDs when taking the exam</p>
                <div className="flex flex-wrap gap-2">
                  {allowedTestIds.map((id, idx) => (
                    <span key={id || idx} className="px-3 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 text-sm rounded-lg border border-indigo-200 hover:border-indigo-300 transition-colors">
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Server Data Table */}
            {serverData.length > 0 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Imported Students</h3>
                      <p className="text-sm text-gray-500 mt-1">{serverData.length} records loaded</p>
                    </div>
                    <div className="mt-2 sm:mt-0">
                      <button
                        onClick={() => navigate(0)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Refresh Data
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-auto max-h-[500px]">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CBT Pass Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test Information</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {serverData.map((row, index) => (
                        <tr key={`${row.cbt_pass_code}-${row.test_id}-${index}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-mono text-gray-900 bg-gray-50 rounded-l-lg">
                            {row.cbt_pass_code}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-700">
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700">
                              {row.test_id}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {row.test_information || <span className="text-gray-400 italic">No information</span>}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <ToastContainer 
            position="top-right" 
            autoClose={4000} 
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </div>
    </div>
  );
};

export default ExcelUpload;